import SectionTitle from "./SectionTitle";
import ScrollReveal from "./ScrollReveal";
import { FaBriefcase } from "react-icons/fa";

interface ProjectGroup {
  name: string;
  domain: string;
  domainColor: string;
  points: string[];
}

interface ExperienceItem {
  title: string;
  company: string;
  location: string;
  period: string;
  current?: boolean;
  projects: ProjectGroup[];
}

const experiences: ExperienceItem[] = [
  {
    title: "Software Engineer",
    company: "Devslane (Escecion Technologies Pvt. Ltd.)",
    location: "Noida, India",
    period: "Aug 2021 - Present",
    current: true,
    projects: [
      {
        name: "AI-Enabled Learning Platform",
        domain: "EdTech",
        domainColor:
          "bg-teal-500/10 text-teal-600 dark:text-teal-400 ring-teal-500/20",
        points: [
          "Architected an end-to-end milestone and rewards (“stars”) system that structured learner progression and drove measurable gains in engagement and lesson completion.",
          "Built adaptive logic to generate customised question sets per learner from real-time performance and skill level, personalizing each learning path.",
          "Developed learner-analytics dashboards in React and TypeScript giving educators real-time visibility into engagement and learning curves across 10,000+ active learners.",
          "Designed and shipped multiple CRUD REST APIs (Node.js, Express, MongoDB) powering core learner workflows, and cut database load through targeted Redis caching.",
          "Implemented an offline-first architecture using IndexedDB with background synchronization for uninterrupted learning in low-connectivity environments.",
          "Integrated AI4Bharat text-to-speech for multilingual audio across 10+ regional languages, and built custom Claude & Cursor agentic workflows that cut repetitive engineering effort by 25%.",
        ],
      },
      {
        name: "Property Management & Booking Platform",
        domain: "PropTech / SaaS",
        domainColor:
          "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 ring-cyan-500/20",
        points: [
          "Built analytics dashboards and booking-synchronization interfaces in React and Redux-Saga, supporting 5,000+ monthly bookings across multiple integrations.",
          "Improved API performance by up to 35% through targeted Redis caching strategies, significantly reducing page load times.",
          "Integrated real-time webhooks from major short-term-rental marketplaces (Airbnb, VRBO) to keep booking data synchronized in PostgreSQL.",
          "Developed REST APIs with Node.js and Express and designed PostgreSQL schemas for bookings, reporting, and asset management.",
          "Integrated Postmark for transactional email delivery and leveraged AWS S3 for reporting and asset storage.",
        ],
      },
    ],
  },
  {
    title: "Software Engineer Intern",
    company: "Devslane (Escecion Technologies Pvt. Ltd.)",
    location: "Noida, India",
    period: "Apr 2021 - Jul 2021",
    projects: [
      {
        name: "Real-Time Social Networking Platform",
        domain: "Social",
        domainColor:
          "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 ring-indigo-500/20",
        points: [
          "Developed core social features (posting, commenting, sharing) using Angular and Angular Material, building reusable, scalable UI components for high interaction volume.",
          "Implemented WebSocket-based real-time updates with RxJS / NgRx state management to handle live activity at scale.",
          "Built supporting REST APIs with Node.js and Express powering core social workflows for 50,000+ users.",
        ],
      },
      {
        name: "Offline-First Field Inspection PWA",
        domain: "Enterprise",
        domainColor:
          "bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-amber-500/20",
        points: [
          "Built an offline-first Progressive Web App enabling field engineers to run inspections in low- or no-connectivity environments, with automatic sync on reconnect.",
          "Implemented complex multi-step forms using Formik (dynamic validation, conditional fields, reusable schemas) on a reusable component architecture, cutting new-form development time by 20%.",
        ],
      },
    ],
  },
];

const Experience = () => {
  return (
    <section id="experience" className="py-14 scroll-mt-16">
      <ScrollReveal>
        <SectionTitle title="Experience" />
      </ScrollReveal>

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-[18px] md:left-[22px] top-2 bottom-2 w-px bg-gradient-to-b from-teal-500 via-cyan-500 to-transparent" />

        <div className="space-y-8">
          {experiences.map((exp, index) => (
            <ScrollReveal key={index} delay={index * 200}>
              <div className="relative pl-12 md:pl-14">
                {/* Timeline dot */}
                <div className="absolute left-2 md:left-3 top-2 w-4 h-4 rounded-full bg-gradient-to-r from-teal-500 to-cyan-500 border-[3px] border-white dark:border-gray-900 z-10 shadow-sm shadow-teal-500/50" />

                <div className="p-5 rounded-2xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 hover:border-teal-500/30 dark:hover:border-teal-500/30 transition-colors card-hover">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <FaBriefcase className="text-teal-500 text-sm" />
                        {exp.title}
                      </h3>
                      <p className="text-teal-600 dark:text-teal-400 font-medium mt-1">
                        {exp.company}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {exp.location}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                        exp.current
                          ? "bg-teal-500/10 text-teal-600 dark:text-teal-400 ring-1 ring-teal-500/20"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                      }`}
                    >
                      {exp.period}
                    </span>
                  </div>

                  <div className="space-y-5">
                    {exp.projects.map((project, pIndex) => (
                      <div
                        key={pIndex}
                        className={
                          pIndex > 0
                            ? "pt-5 border-t border-gray-200/70 dark:border-gray-700/50"
                            : ""
                        }
                      >
                        <div className="flex items-center flex-wrap gap-2 mb-3">
                          <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                            {project.name}
                          </h4>
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium ring-1 ${project.domainColor}`}
                          >
                            {project.domain}
                          </span>
                        </div>

                        <ul className="space-y-2">
                          {project.points.map((point, i) => (
                            <li
                              key={i}
                              className="flex gap-3 text-gray-700 dark:text-gray-300 text-sm md:text-base leading-relaxed"
                            >
                              <span className="text-teal-500 mt-1.5 flex-shrink-0 text-xs">
                                &#9670;
                              </span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
